import React from 'react';
import { motion } from 'framer-motion';
import { Counter } from './ui/Counter';
// Company logos as components for better visual representation
const CompanyLogo = ({
  name
}: {
  name: string;
}) => {
  const getLogoStyle = () => {
    switch (name) {
      case 'Total':
        return 'text-blue-900 font-bold tracking-tight';
      case 'Bouygues':
        return 'text-blue-700 font-bold';
      case 'Vinci':
        return 'text-yellow-600 font-bold tracking-wide';
      case 'Eiffage':
        return 'text-orange-600 font-extrabold';
      case 'Suez':
        return 'text-blue-500 font-bold';
      case 'BNP Paribas':
        return 'text-green-700 font-semibold';
      default:
        return 'text-gray-800';
    }
  };
  return <div className="h-12 flex items-center justify-center px-4 py-2 bg-white rounded-lg shadow-sm" data-id="element-836">
      <span className={`text-xl ${getLogoStyle()}`} data-id="element-837">{name}</span>
    </div>;
};
export function SocialProofSection() {
  return <section className="py-20 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden" data-id="element-838">
      {/* Decorative elements */}
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-blue-100 to-transparent" data-id="element-839"></div>
      <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-50 rounded-full opacity-50" data-id="element-840"></div>
      <div className="absolute -bottom-20 -right-20 w-64 h-64 bg-yellow-50 rounded-full opacity-30" data-id="element-841"></div>
      <div className="container mx-auto px-4 relative z-10" data-id="element-842">
        <div className="text-center mb-16" data-id="element-843">
          <span className="inline-block px-3 py-1 bg-blue-50 text-blue-900 text-sm font-semibold rounded-full mb-4" data-id="element-844">
            Références clients
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-blue-900 mb-4" data-id="element-845">
            Ils nous font confiance
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg" data-id="element-846">
            Des entreprises de toutes tailles utilisent ElevateIT pour accéder
            aux marchés africains et développer leur business
          </p>
        </div>
        {/* Client logos */}
        <div className="flex flex-wrap justify-center items-center gap-8 md:gap-12 mb-20" data-id="element-847">
          {['Total', 'Bouygues', 'Vinci', 'Eiffage', 'Suez', 'BNP Paribas'].map((company, index) => <motion.div key={company} className="transition-all duration-300 hover:scale-105" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} viewport={{
          once: true
        }} data-id="element-848">
                <CompanyLogo name={company} data-id="element-849" />
              </motion.div>)}
        </div>
        {/* Stats counters */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-850">
          <motion.div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center transform transition-transform hover:-translate-y-1" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} viewport={{
          once: true
        }} data-id="element-851">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4" data-id="element-852">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-853">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" data-id="element-854" />
              </svg>
            </div>
            <Counter end={1000} prefix="+" className="text-4xl md:text-5xl font-bold text-blue-900 mb-2" data-id="element-855" />
            <p className="text-gray-600 font-medium" data-id="element-856">appels d'offres/jour</p>
          </motion.div>
          <motion.div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center transform transition-transform hover:-translate-y-1" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} viewport={{
          once: true
        }} data-id="element-857">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4" data-id="element-858">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-859">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-860" />
              </svg>
            </div>
            <Counter end={30} prefix="+" className="text-4xl md:text-5xl font-bold text-blue-900 mb-2" data-id="element-861" />
            <p className="text-gray-600 font-medium" data-id="element-862">pays couverts</p>
          </motion.div>
          <motion.div className="bg-white p-8 rounded-xl shadow-lg border border-gray-100 text-center transform transition-transform hover:-translate-y-1" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.4
        }} viewport={{
          once: true
        }} data-id="element-863">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4" data-id="element-864">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-blue-900" fill="none" viewBox="0 0 24 24" stroke="currentColor" data-id="element-865">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-866" />
              </svg>
            </div>
            <Counter end={10} prefix="+" className="text-4xl md:text-5xl font-bold text-blue-900 mb-2" data-id="element-867" />
            <p className="text-gray-600 font-medium" data-id="element-868">ans de data historique</p>
          </motion.div>
        </div>
      </div>
    </section>;
}